import {
  CenterLinePoint,
  SavedAnalysis,
  ThermoDetail,
  ThermoHeightReferenceLine
} from "../types";

export type ThermoHeightCalibration = {
  sampleIds: number[];
  referenceSampleId: number;
  referenceLine: ThermoHeightReferenceLine;
  referenceHeightMm: number;
};

function sortNumericAsc(values: number[]) {
  return [...values].sort((left, right) => left - right);
}

export function buildSampleIdSignature(sampleIds: Iterable<number>) {
  return sortNumericAsc(
    Array.from(sampleIds).filter((value) => Number.isFinite(value))
  ).join(",");
}

export function heightCalibrationFromSavedAnalysis(
  analysis: Pick<
    SavedAnalysis,
    "sample_id_array" | "height_reference_sample_id" | "height_reference_line" | "height_reference_height_mm"
  >
): ThermoHeightCalibration | null {
  const referenceSampleId =
    analysis.height_reference_sample_id == null
      ? Number.NaN
      : Number(analysis.height_reference_sample_id);
  const referenceHeightMm =
    analysis.height_reference_height_mm == null
      ? Number.NaN
      : Number(analysis.height_reference_height_mm);
  const referenceLine = analysis.height_reference_line;
  if (
    !Number.isFinite(referenceSampleId) ||
    !Number.isFinite(referenceHeightMm) ||
    referenceHeightMm <= 0 ||
    !referenceLine ||
    !isFiniteLine(referenceLine)
  ) {
    return null;
  }
  const sampleIds = (analysis.sample_id_array || []).map(Number).filter(Number.isFinite);
  return {
    sampleIds,
    referenceSampleId,
    referenceLine,
    referenceHeightMm
  };
}

function isFiniteLine(line: ThermoHeightReferenceLine) {
  return (
    Number.isFinite(line.x0) &&
    Number.isFinite(line.y0) &&
    Number.isFinite(line.x1) &&
    Number.isFinite(line.y1)
  );
}

type OrderedPoint = {
  point: CenterLinePoint;
  x: number;
  y: number;
  distance: number;
};

function buildOrderedCenterLine(points: CenterLinePoint[]): OrderedPoint[] {
  const validPoints = points.filter(
    (point): point is CenterLinePoint & { x: number } =>
      Number.isFinite(point.y) && Number.isFinite(point.x)
  );
  if (!validPoints.length) return [];
  if (validPoints.length === 1) {
    const [single] = validPoints;
    return [{ point: single, x: single.x, y: single.y, distance: 0 }];
  }

  const centroid = validPoints.reduce(
    (acc, point) => ({ x: acc.x + point.x, y: acc.y + point.y }),
    { x: 0, y: 0 }
  );
  const meanX = centroid.x / validPoints.length;
  const meanY = centroid.y / validPoints.length;

  let covXX = 0;
  let covXY = 0;
  let covYY = 0;
  validPoints.forEach((point) => {
    const dx = point.x - meanX;
    const dy = point.y - meanY;
    covXX += dx * dx;
    covXY += dx * dy;
    covYY += dy * dy;
  });

  const theta = 0.5 * Math.atan2(2 * covXY, covXX - covYY);
  const axisX = Math.cos(theta);
  const axisY = Math.sin(theta);

  const ordered = [...validPoints]
    .map((point) => ({
      point,
      x: point.x,
      y: point.y,
      projection: (point.x - meanX) * axisX + (point.y - meanY) * axisY
    }))
    .sort((left, right) => {
      if (left.projection !== right.projection) return left.projection - right.projection;
      if (left.y !== right.y) return left.y - right.y;
      return left.x - right.x;
    });

  const first = ordered[0];
  const last = ordered[ordered.length - 1];
  if (first.y > last.y || (first.y === last.y && first.x > last.x)) {
    ordered.reverse();
  }

  let cumulative = 0;
  return ordered.map((entry, index) => {
    if (index > 0) {
      const previous = ordered[index - 1];
      cumulative += Math.hypot(entry.x - previous.x, entry.y - previous.y);
    }
    return {
      point: entry.point,
      x: entry.x,
      y: entry.y,
      distance: cumulative
    };
  });
}

function findNearestOrderedIndex(orderedPoints: OrderedPoint[], target: { x: number; y: number }) {
  let bestIndex = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  orderedPoints.forEach((entry, index) => {
    const distance = Math.hypot(entry.x - target.x, entry.y - target.y);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  });
  return bestIndex;
}

function projectReferenceTotalHeightMm(
  referenceDetail: ThermoDetail,
  calibration: ThermoHeightCalibration
) {
  const ordered = buildOrderedCenterLine(referenceDetail.centerLine);
  if (ordered.length < 2) return null;

  const startIndex = findNearestOrderedIndex(ordered, {
    x: calibration.referenceLine.x0,
    y: calibration.referenceLine.y0
  });
  const endIndex = findNearestOrderedIndex(ordered, {
    x: calibration.referenceLine.x1,
    y: calibration.referenceLine.y1
  });
  if (startIndex < 0 || endIndex < 0 || startIndex === endIndex) return null;

  const firstIndex = Math.min(startIndex, endIndex);
  const lastIndex = Math.max(startIndex, endIndex);
  const referenceSegmentLengthPx = ordered[lastIndex].distance - ordered[firstIndex].distance;
  const totalLengthPx = ordered[ordered.length - 1].distance;
  if (
    !Number.isFinite(referenceSegmentLengthPx) ||
    referenceSegmentLengthPx <= 0 ||
    !Number.isFinite(totalLengthPx) ||
    totalLengthPx <= 0
  ) {
    return null;
  }

  return (totalLengthPx / referenceSegmentLengthPx) * calibration.referenceHeightMm;
}

export function projectDetailCenterLineToHeight(
  detail: ThermoDetail,
  totalHeightMm: number
): CenterLinePoint[] {
  const ordered = buildOrderedCenterLine(detail.centerLine);
  if (!ordered.length) return [];
  const totalLengthPx = ordered[ordered.length - 1].distance;
  if (
    !Number.isFinite(totalLengthPx) ||
    totalLengthPx <= 0 ||
    !Number.isFinite(totalHeightMm) ||
    totalHeightMm <= 0
  ) {
    return [];
  }

  return ordered.map(({ point, distance }) => ({
    ...point,
    height_mm: (distance / totalLengthPx) * totalHeightMm
  }));
}

type InterpolatedPoint = {
  temperature: number;
  temperature_epsilon_0875: number;
  temperature_upper: number;
  temperature_lower: number;
  viscosity: number;
};

function interpolateValue(
  leftHeight: number,
  rightHeight: number,
  leftValue: number,
  rightValue: number,
  targetHeight: number
) {
  if (Math.abs(rightHeight - leftHeight) < 1e-6) return leftValue;
  const t = (targetHeight - leftHeight) / (rightHeight - leftHeight);
  return leftValue + (rightValue - leftValue) * t;
}

function interpolateProjectedPoint(points: CenterLinePoint[], targetHeightMm: number): InterpolatedPoint | null {
  const valid = points.filter((point) => Number.isFinite(point.height_mm));
  if (!valid.length) return null;
  if (targetHeightMm < Number(valid[0].height_mm) || targetHeightMm > Number(valid[valid.length - 1].height_mm)) {
    return null;
  }
  for (let index = 0; index < valid.length; index += 1) {
    const point = valid[index];
    const pointHeight = Number(point.height_mm);
    if (Math.abs(pointHeight - targetHeightMm) < 1e-6) {
      return {
        temperature: point.temperature,
        temperature_epsilon_0875: point.temperature_epsilon_0875 ?? point.temperature,
        temperature_upper: point.temperature_upper ?? point.temperature,
        temperature_lower: point.temperature_lower ?? point.temperature,
        viscosity: point.viscosity
      };
    }
    const next = valid[index + 1];
    if (!next) break;
    const nextHeight = Number(next.height_mm);
    if (targetHeightMm > nextHeight) continue;
    return {
      temperature: interpolateValue(
        pointHeight,
        nextHeight,
        point.temperature,
        next.temperature,
        targetHeightMm
      ),
      temperature_epsilon_0875: interpolateValue(
        pointHeight,
        nextHeight,
        point.temperature_epsilon_0875 ?? point.temperature,
        next.temperature_epsilon_0875 ?? next.temperature,
        targetHeightMm
      ),
      temperature_upper: interpolateValue(
        pointHeight,
        nextHeight,
        point.temperature_upper ?? point.temperature,
        next.temperature_upper ?? next.temperature,
        targetHeightMm
      ),
      temperature_lower: interpolateValue(
        pointHeight,
        nextHeight,
        point.temperature_lower ?? point.temperature,
        next.temperature_lower ?? next.temperature,
        targetHeightMm
      ),
      viscosity: interpolateValue(
        pointHeight,
        nextHeight,
        point.viscosity,
        next.viscosity,
        targetHeightMm
      )
    };
  }
  return null;
}

export function aggregateCenterLinesByProjectedHeight(
  details: ThermoDetail[],
  calibration: ThermoHeightCalibration
): CenterLinePoint[] | null {
  const referenceDetail = details.find((detail) => detail.id === calibration.referenceSampleId);
  if (!referenceDetail) return null;
  const totalHeightMm = projectReferenceTotalHeightMm(referenceDetail, calibration);
  if (!Number.isFinite(totalHeightMm) || totalHeightMm == null || totalHeightMm <= 0) {
    return null;
  }

  const projectedReference = projectDetailCenterLineToHeight(referenceDetail, totalHeightMm);
  if (!projectedReference.length) return null;
  const projectedBySample = new Map<number, CenterLinePoint[]>();
  details.forEach((detail) => {
    projectedBySample.set(detail.id, projectDetailCenterLineToHeight(detail, totalHeightMm));
  });

  const aggregated: Array<CenterLinePoint | null> = projectedReference
    .map((referencePoint) => {
      const targetHeight = Number(referencePoint.height_mm);
      let temperatureSum = 0;
      let temperature0875Sum = 0;
      let temperatureUpperSum = 0;
      let temperatureLowerSum = 0;
      let viscositySum = 0;
      let count = 0;

      projectedBySample.forEach((projectedPoints) => {
        const interpolated = interpolateProjectedPoint(projectedPoints, targetHeight);
        if (!interpolated) return;
        temperatureSum += interpolated.temperature;
        temperature0875Sum += interpolated.temperature_epsilon_0875;
        temperatureUpperSum += interpolated.temperature_upper;
        temperatureLowerSum += interpolated.temperature_lower;
        viscositySum += interpolated.viscosity;
        count += 1;
      });

      if (!count) return null;
      return {
        ...referencePoint,
        height_mm: targetHeight,
        temperature: temperatureSum / count,
        temperature_epsilon_0875: temperature0875Sum / count,
        temperature_upper: temperatureUpperSum / count,
        temperature_lower: temperatureLowerSum / count,
        viscosity: viscositySum / count
      } satisfies CenterLinePoint;
    });

  return aggregated.filter((point): point is CenterLinePoint => point !== null);
}
